import React, { Component } from 'react';
import { connect } from 'react-redux';
import ScrollArea from 'react-scrollbar';
import SelectField from 'material-ui/SelectField';
import MenuItem from 'material-ui/MenuItem';

import TextField from '../../components/basic/TextField';
import Button from '../../components/basic/Button';
import Toggle from '../../components/basic/Toggle';
import ModelPicker from '../../components/basic/ModelPicker';
import { Section, SectionItem } from '../../components/basic/section';
import JsonViewer from '../../components/basic/JsonViewer';
import { DEFAULT_LLM_MODEL } from '../../constants/Constants';

class EchoPulseFullTesterContainer extends Component {
  constructor(props) {
    super(props);

    // Initialize state
    this.state = {
      errors: {},
      // Form state
      formData: {
        projectId: 'c07d9bb8-06d1-436b-a787-1b916d64e63b',
        teamId: '',
        // Common model params (defaults)
        commonParams: {
          temperature: 0,
          maxTokens: 32000,
          model: DEFAULT_LLM_MODEL.value,
          location: ''
        },
        // Initial prompt
        prompt: {
          promptTemplate: '',
          language: 'en',
          // Override params (if empty, use common)
          temperature: '',
          maxTokens: '',
          model: '',
          location: ''
        },
        // Context pass prompt (optional)
        contextPassPrompt: {
          promptTemplate: '',
          language: 'en',
          enabled: false,
          temperature: '',
          maxTokens: '',
          model: '',
          location: ''
        },
        // Translation prompt
        translationPrompt: {
          promptTemplate: '',
          language: 'cs',
          temperature: '',
          maxTokens: '',
          model: '',
          location: ''
        }
      },
      // Results state
      result: { data: null, error: null },
      // Loading state
      loading: false,
      resultLang: null
    };
  }

  // Handle form input changes
  changeFormData = (name, value) => {
    this.setState(prevState => ({
      formData: {
        ...prevState.formData,
        [name]: value
      }
    }));
  };

  changeNestedFormData = (parentKey, childKey, value) => {
    this.setState(prevState => ({
      formData: {
        ...prevState.formData,
        [parentKey]: {
          ...prevState.formData[parentKey],
          [childKey]: value
        }
      }
    }));
  };

  handleSetResultLang = resultLang => {
    this.setState({ resultLang });
  };

  handleCopyToClipboard = async data => {
    try {
      const jsonString = JSON.stringify(data, null, 2);
      await navigator.clipboard.writeText(jsonString);
      // You could add a toast notification here if available
      alert('Result copied to clipboard!');
    } catch (error) {
      console.error('Failed to copy to clipboard:', error);
      // Fallback for older browsers
      try {
        const textArea = document.createElement('textarea');
        textArea.value = JSON.stringify(data, null, 2);
        textArea.style.position = 'fixed';
        textArea.style.left = '-999999px';
        document.body.appendChild(textArea);
        textArea.select();
        document.execCommand('copy');
        document.body.removeChild(textArea);
        alert('Result copied to clipboard!');
      } catch (fallbackError) {
        console.error('Fallback copy failed:', fallbackError);
        alert('Failed to copy to clipboard');
      }
    }
  };

  handleChange = e => {
    const { name, value } = e.target;
    this.changeFormData(name, value);
  };

  handleCommonParamsChange = (name, value) => {
    this.changeNestedFormData('commonParams', name, value);
  };

  // Validate prompt template contains ${input}
  validatePromptTemplate = (promptTemplate, promptKey) => {
    if (!promptTemplate || !promptTemplate.trim()) {
      return null; // Empty templates are handled elsewhere
    }
    if (!promptTemplate.includes('${input}')) {
      return `${promptKey === 'contextPassPrompt' ? 'Context Pass' : 'Translation'} prompt template must contain \${input}`;
    }
    return null;
  };

  handlePromptChange = (promptKey, name, value) => {
    this.changeNestedFormData(promptKey, name, value);

    // Validate promptTemplate for contextPassPrompt and translationPrompt
    if (name === 'promptTemplate' && (promptKey === 'contextPassPrompt' || promptKey === 'translationPrompt')) {
      const error = this.validatePromptTemplate(value, promptKey);
      this.setState(prevState => {
        const errors = { ...prevState.errors };
        if (error) {
          errors[`${promptKey}Template`] = error;
        } else {
          // Clear error if validation passes
          delete errors[`${promptKey}Template`];
        }
        return { errors };
      });
    }
  };

  handleLanguageChange = (promptKey, e, idx, value) => {
    this.handlePromptChange(promptKey, 'language', value);
  };

  handleModelChange = (promptKey, e, idx, value) => {
    this.handlePromptChange(promptKey, 'model', value);
  };

  handleCommonModelChange = (e, idx, value) => {
    this.handleCommonParamsChange('model', value);
  };

  // Get effective model params for a prompt
  getEffectiveParams = promptKey => {
    const { formData } = this.state;
    const prompt = formData[promptKey];
    const common = formData.commonParams;

    return {
      temperature: prompt.temperature !== '' ? parseFloat(prompt.temperature) : common.temperature,
      maxTokens: prompt.maxTokens !== '' ? parseInt(prompt.maxTokens) : common.maxTokens,
      model: prompt.model !== '' ? prompt.model : common.model,
      location: prompt.location !== '' ? prompt.location : common.location
    };
  };

  // Build request body
  buildRequestBody = () => {
    const { formData } = this.state;

    const buildPromptDto = promptKey => {
      const prompt = formData[promptKey];
      const params = this.getEffectiveParams(promptKey);

      if (!prompt.promptTemplate || !prompt.promptTemplate.trim()) {
        return null;
      }

      return {
        promptTemplate: prompt.promptTemplate.trim(),
        language: prompt.language.toUpperCase(),
        params: {
          temperature: params.temperature,
          maxTokens: params.maxTokens,
          model: params.model,
          location: params.location && String(params.location).trim() !== '' ? params.location : null
        }
      };
    };

    const body = {
      projectId: formData.projectId,
      prompt: buildPromptDto('prompt'),
      translationPrompt: buildPromptDto('translationPrompt')
    };

    if (formData.teamId && formData.teamId.trim()) {
      body.teamId = formData.teamId.trim();
    }

    if (formData.contextPassPrompt.enabled) {
      const contextPass = buildPromptDto('contextPassPrompt');
      if (contextPass) {
        body.contextPassPrompt = contextPass;
      }
    }

    return body;
  };

  // Handle form submission
  handleSubmit = async e => {
    const { auth } = this.props;

    e.preventDefault();
    this.setState({ loading: true, errors: {}, result: { data: null, error: null } });

    try {
      const { formData } = this.state;

      if (!formData.projectId || !formData.projectId.trim()) {
        this.setState({
          errors: {
            projectId: 'Project ID is required'
          }
        });
        throw new Error('Project ID is required');
      }

      const body = this.buildRequestBody();

      if (!body.prompt) {
        this.setState({
          errors: {
            promptTemplate: 'Initial prompt is required'
          }
        });
        throw new Error('Initial prompt is required');
      }

      if (!body.translationPrompt) {
        this.setState({
          errors: {
            translationPromptTemplate: 'Translation prompt is required'
          }
        });
        throw new Error('Translation prompt is required');
      }

      // Validate contextPassPrompt.promptTemplate if enabled
      if (
        formData.contextPassPrompt.enabled &&
        formData.contextPassPrompt.promptTemplate &&
        formData.contextPassPrompt.promptTemplate.trim()
      ) {
        const contextPassError = this.validatePromptTemplate(
          formData.contextPassPrompt.promptTemplate,
          'contextPassPrompt'
        );
        if (contextPassError) {
          this.setState({
            errors: {
              contextPassPromptTemplate: contextPassError
            }
          });
          throw new Error(contextPassError);
        }
      }

      // Validate translationPrompt.promptTemplate
      if (formData.translationPrompt.promptTemplate && formData.translationPrompt.promptTemplate.trim()) {
        const translationError = this.validatePromptTemplate(
          formData.translationPrompt.promptTemplate,
          'translationPrompt'
        );
        if (translationError) {
          this.setState({
            errors: {
              translationPromptTemplate: translationError
            }
          });
          throw new Error(translationError);
        }
      }

      const response = await fetch('/api/v1/admin/ai/digests/echo-pulse/generate-full', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${auth.access_token}`
        },
        body: JSON.stringify(body)
      });

      const { payload, error, errorMessage } = await response.json();
      const err = error || errorMessage;

      if (err) {
        throw new Error(err);
      }

      this.setState({
        result: {
          data: payload,
          error: null
        }
      });
    } catch (error) {
      console.error('Error testing echo pulse full:', error);
      this.setState({
        result: {
          data: null,
          error: `Error: ${error.message}`
        }
      });
    } finally {
      this.setState({ loading: false });
    }
  };

  renderPromptSection = (promptKey, title, required = false, enabled = true, onEnabledChange = null) => {
    const { formData, errors } = this.state;
    const prompt = formData[promptKey];
    const params = this.getEffectiveParams(promptKey);

    return (
      <Section title={title}>
        {onEnabledChange && (
          <SectionItem>
            <Toggle
              id={`${promptKey}Enabled`}
              name={`${promptKey}Enabled`}
              value={enabled}
              onChange={onEnabledChange}
              label={`Enable ${title}`}
              fullWidth
            />
          </SectionItem>
        )}
        <SectionItem contentStyle={{ paddingTop: 0 }}>
          <TextField
            id={`${promptKey}Template`}
            name={`${promptKey}Template`}
            value={prompt.promptTemplate}
            onChange={e => this.handlePromptChange(promptKey, 'promptTemplate', e.target.value)}
            label={`${title} Template${required ? ' *' : ''}`}
            hintText={`Enter ${title.toLowerCase()} template here`}
            fullWidth
            multiLine
            rows={6}
            rowsMax={6}
            inputStyle={{ fontFamily: 'monospace', fontSize: '10px', lineHeight: '14px' }}
            errorText={errors[`${promptKey}Template`]}
            disabled={!enabled}
          />
        </SectionItem>

        <SectionItem contentStyle={{ paddingTop: 0 }}>
          <div className="row">
            <div className="col-sm-6">
              <SelectField
                id={`${promptKey}Language`}
                name={`${promptKey}Language`}
                value={prompt.language}
                onChange={(e, idx, value) => this.handleLanguageChange(promptKey, e, idx, value)}
                floatingLabelText={'Language'}
                floatingLabelFixed
                hintText={'Language'}
                fullWidth
                style={{ fontSize: '14px' }}
                disabled={!enabled}
              >
                <MenuItem key="en" value="en" primaryText="English" />
                <MenuItem key="cs" value="cs" primaryText="Česky" />
              </SelectField>
            </div>
            <div className="col-sm-6">
              <ModelPicker
                id={`${promptKey}Model`}
                name={`${promptKey}Model`}
                value={prompt.model || ''}
                onChange={value => this.handleModelChange(promptKey, null, null, value)}
                floatingLabelText={'Model (override)'}
                floatingLabelFixed
                hintText={`Default: ${params.model}`}
                fullWidth
                style={{ fontSize: '14px' }}
                disabled={!enabled}
              />
            </div>
          </div>
        </SectionItem>

        <SectionItem contentStyle={{ paddingTop: 0 }}>
          <div className="row">
            <div className="col-sm-6">
              <TextField
                id={`${promptKey}Temperature`}
                name={`${promptKey}Temperature`}
                value={prompt.temperature}
                onChange={e => this.handlePromptChange(promptKey, 'temperature', e.target.value)}
                fullWidth
                label={`Temperature (override)`}
                hintText={`Default: ${params.temperature}`}
                helperText="Temperature for the model"
                type="number"
                min={0}
                max={2}
                step={0.05}
                disabled={!enabled}
              />
            </div>
            <div className="col-sm-6">
              <TextField
                id={`${promptKey}MaxTokens`}
                name={`${promptKey}MaxTokens`}
                value={prompt.maxTokens}
                onChange={e => this.handlePromptChange(promptKey, 'maxTokens', e.target.value)}
                fullWidth
                label={`Max tokens (override)`}
                hintText={`Default: ${params.maxTokens}`}
                helperText="Maximum number of tokens to generate"
                type="number"
                min={1}
                max={65535}
                disabled={!enabled}
              />
            </div>
          </div>
        </SectionItem>

        <SectionItem contentStyle={{ paddingTop: 0 }}>
          <TextField
            id={`${promptKey}Location`}
            name={`${promptKey}Location`}
            value={prompt.location}
            onChange={e => this.handlePromptChange(promptKey, 'location', e.target.value)}
            fullWidth
            label={`Location (override)`}
            hintText={`Default: ${params.location || ''}`}
            disabled={!enabled}
          />
        </SectionItem>
      </Section>
    );
  };

  render() {
    const { formData, result, loading, errors } = this.state;
    const resultData = result.data;
    let resultLang = null;
    let digestObject = null;
    let digestObjects = null;
    if (resultData && resultData.digestObjects) {
      resultLang = this.state.resultLang || Object.keys(resultData.digestObjects)[0];
      digestObjects = resultData.digestObjects;
      digestObject = digestObjects[resultLang];
    }

    return (
      <form className="Form" onSubmit={this.handleSubmit}>
        <ScrollArea>
          <div className="Form-content-container">
            <div className="container">
              <div className="row">
                <div className="col-md-6">
                  <Section title="Echo Pulse Full Tester">
                    <SectionItem contentStyle={{ paddingTop: 0 }}>
                      <TextField
                        type="text"
                        id="projectId"
                        name="projectId"
                        value={formData.projectId}
                        onChange={this.handleChange}
                        fullWidth
                        label="Project ID *"
                        hintText="Enter project ID"
                        errorText={errors.projectId}
                      />
                    </SectionItem>

                    <SectionItem contentStyle={{ paddingTop: 0 }}>
                      <TextField
                        type="text"
                        id="teamId"
                        name="teamId"
                        value={formData.teamId}
                        onChange={this.handleChange}
                        fullWidth
                        label="Team ID (optional)"
                        hintText="Enter team ID"
                        errorText={errors.teamId}
                      />
                    </SectionItem>
                  </Section>

                  <Section title="Common Model Parameters">
                    <SectionItem contentStyle={{ paddingTop: 0 }}>
                      <div className="row">
                        <div className="col-sm-6">
                          <TextField
                            id="commonMaxTokens"
                            name="maxTokens"
                            value={formData.commonParams.maxTokens}
                            onChange={e => this.handleCommonParamsChange('maxTokens', e.target.value)}
                            fullWidth
                            label="Max tokens"
                            hintText="Enter max tokens"
                            helperText="Maximum number of tokens to generate"
                            type="number"
                            min={1}
                            max={8192}
                          />
                        </div>
                        <div className="col-sm-6">
                          <TextField
                            id="commonTemperature"
                            name="temperature"
                            value={formData.commonParams.temperature}
                            onChange={e => this.handleCommonParamsChange('temperature', e.target.value)}
                            fullWidth
                            label="Temperature"
                            hintText="Enter temperature"
                            helperText="Temperature for the model"
                            type="number"
                            min={0}
                            max={2}
                            step={0.05}
                          />
                        </div>
                      </div>

                      <ModelPicker
                        id="commonModel"
                        name="model"
                        value={formData.commonParams.model}
                        onChange={value => this.handleCommonModelChange(null, null, value)}
                        floatingLabelText={'Model'}
                        floatingLabelFixed
                        hintText={'Model'}
                        fullWidth
                        style={{ fontSize: '14px' }}
                      />

                      <TextField
                        id="commonLocation"
                        name="location"
                        value={formData.commonParams.location}
                        onChange={e => this.handleCommonParamsChange('location', e.target.value)}
                        fullWidth
                        label="Location"
                        hintText="Enter location (optional)"
                      />
                    </SectionItem>
                  </Section>

                  {this.renderPromptSection('prompt', 'Initial Prompt', true, true)}

                  {this.renderPromptSection(
                    'contextPassPrompt',
                    'Context Pass Prompt',
                    false,
                    formData.contextPassPrompt.enabled,
                    e => this.handlePromptChange('contextPassPrompt', 'enabled', e.target.checked)
                  )}

                  {this.renderPromptSection('translationPrompt', 'Translation Prompt', true, true)}
                </div>

                <div className="col-md-6 flex flex1 flex-column" style={{ paddingBottom: '0.5em' }}>
                  <div className="Section-title">
                    <span>Results</span>
                  </div>
                  <div
                    className="Section-content flex flex1 flex-column"
                    style={!result.error && !digestObjects ? { boxShadow: 'none', background: '#e0e0e0' } : {}}
                  >
                    {result.error && (
                      <div className="Text-error Text-regular" style={{ padding: '1rem' }}>
                        {result.error}
                      </div>
                    )}
                    {digestObjects && (
                      <div className="flex flex-column flex1">
                        <div className="flex flex-row" style={{ gap: '0.5em', padding: '0.5em' }}>
                          {Object.keys(digestObjects).map(key => {
                            return (
                              <Button
                                key={key}
                                className="flex1"
                                label={key}
                                onClick={() => this.handleSetResultLang(key)}
                                primary={resultLang === key}
                                raised={resultLang === key}
                              />
                            );
                          })}
                        </div>
                        <JsonViewer data={digestObject} />
                        <div className="flex flex-column" style={{ padding: '0.5em' }}>
                          <Button label="Copy Result" onClick={() => this.handleCopyToClipboard(digestObject)} />
                        </div>
                      </div>
                    )}
                    {!result.error && !digestObjects && (
                      <div className="text-muted" style={{ padding: '2rem 1rem', textAlign: 'center' }}>
                        <div className="Text-regular">No results yet</div>
                        <div className="Text-small">Click "Run" to generate results</div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </ScrollArea>
        <div className="Form-buttons-container" style={{ overflow: 'hidden' }}>
          <div className="Form-buttons flex flex-align-center" style={{ gap: '1rem', justifyContent: 'flex-end' }}>
            <div>
              <Button label="Run" primary raised loading={loading} type="submit" />
            </div>
          </div>
        </div>
      </form>
    );
  }
}

const mapStateToProps = ({ auth }) => ({ auth });

export default connect(mapStateToProps)(EchoPulseFullTesterContainer);



// WEBPACK FOOTER //
// ./src/containers/private/EchoPulseFullTesterContainer.js